import React, { useEffect, useMemo } from 'react'
import { Check, Clock, User, X, Layers } from 'lucide-react';
import { dispatchHandler } from '../../../helper/apiHelperFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { getPermissionKey } from '../../../helper/permissions';
import NoDataFound from '../../../genericComponents/NoDataFound';


function DesignStagesModal(props) {

    const { detailsModal = {}, documentInfo = {} } = props;

    const dispatch = useDispatch();
    const orderHistory = useSelector(state => state?.administrationStore?.entpoGetAllTnaOrderHistoryData);
    const statusConfig = useSelector(state => state?.administrationStore?.tnaGetAllStatusConfigData);
    const isLoading = !!orderHistory?.isLoading || !!statusConfig?.isLoading;

    useEffect(() => {
        dispatchHandler(dispatch, "entpoGetAllTnaOrderHistoryRequest", {designNo: detailsModal?.design_no || "", orderNo: documentInfo?.documentNo || ""}, getPermissionKey("dashboard", "tna", "view"))
        // All configured stages — drives the full timeline so even not-yet-done
        // stages render (the history API only returns completed ones).
        dispatchHandler(dispatch, "tnaGetAllStatusConfigRequest", {}, getPermissionKey("dashboard", "tna", "view"))
    }, [])

    // Build the full timeline from the configured stage list, then merge in the
    // completed-stage details from order history:
    //   - every configured stage (tnaGetAllStatusConfig) shows, in statusOrder
    //   - if a matching history entry exists (matched by stage name), it's DONE:
    //       user   ← createdBy
    //       date   ← actionTime
    //       status ← DELAYED when poConfimedOverAgeing > 0, else ON TRACK
    //   - stages with no history entry are PENDING (neutral, not yet actioned)
    const stages = useMemo(() => {
        const configResource = statusConfig?.data?.resource || [];
        const historyResource = orderHistory?.data?.resource || [];

        // Index history by stage name for O(1) lookup. If the same stage appears
        // more than once, the last entry wins (most recent action).
        const historyByName = {};
        historyResource.forEach(item => {
            if (item?.status) historyByName[item.status] = item;
        });

        const configured = [...configResource]
            .filter(s => s?.statusName)
            .sort((a, b) => (a.statusOrder ?? 0) - (b.statusOrder ?? 0));

        return configured.map((stage, idx) => {
            const done = historyByName[stage.statusName];
            return {
                id: stage?.id ?? stage?.statusOrder ?? idx + 1,
                name: stage.statusName,
                user: done?.createdBy || '–',
                date: done?.actionTime || '–',
                status: done?.ontrackStatus == "delayed" ? 'DELAYED' : done?.ontrackStatus == "on-track" ? 'ON TRACK' : 'PENDING',
            };
        });
    }, [statusConfig?.data?.resource, orderHistory?.data?.resource]);

    const getStatusBadge = (status) => {
        if (status === 'ON TRACK') {
            return (
                <span className="inline-block text-center py-[4px] px-[10px] bg-[#03B675] text-white text-[10px] font-semibold rounded-[4px] uppercase tracking-wider shadow-sm">
                    ON TRACK
                </span>
            );
        } else if (status === 'DELAYED') {
            return (
                <span className="inline-block text-center py-[4px] px-[10px] bg-[#FD3757] text-white text-[10px] font-semibold rounded-[4px] uppercase tracking-wider shadow-sm">
                    DELAYED
                </span>
            );
        } else {
            return (
                <span className="inline-block text-center py-[4px] px-[10px] bg-[#67768E] text-white text-[10px] font-semibold rounded-[4px] uppercase tracking-wider shadow-sm">
                    ACTION DUE
                </span>
            );
        }
    };

    const getIcon = (stage, index) => {
        if (stage.status === 'ON TRACK') {
            return (
                <div className="relative w-[22px] h-[22px] rounded-full bg-[#00B074]/20 flex items-center justify-center">
                    <div className="w-[85%] h-[85%] bg-[#00B074] rounded-full flex items-center justify-center flex-shrink-0 z-10 shadow-sm">
                        <Check className="w-[12px] h-[12px] text-white stroke-[3px]" />
                    </div>
                </div>
            );
        } else if (stage.status === 'DELAYED') {
            return (
                <div className="w-[22px] h-[22px] bg-[#FD3757] rounded-full flex items-center justify-center flex-shrink-0 z-10 shadow-sm">
                    <Clock className="w-[16px] h-[16px] text-white stroke-[2px]" />
                </div>
            );
        } else {
            return (
                <div className="w-[22px] h-[22px] bg-[#F0F3F6] border-[1px] border-[#DAE4EE] rounded-full flex items-center justify-center flex-shrink-0 text-[#0F172A] font-semibold text-[10px] z-10 shadow-sm">
                    {index + 1}
                </div>
            );
        }
    };

    return (
        <div className="h-full flex flex-col bg-white select-none w-full">
            {/* Header */}
            <div className="border-b border-gray-100 px-[15px] py-[10px] flex items-center justify-between bg-white">
                <div className="flex items-start gap-3">
                    <Layers className="w-[15px] h-[15px] text-[#6A6C70] mt-0.5 flex-shrink-0" />
                    <div className="flex flex-col gap-0.5">
                        <h1 className="text-[12px] font-semibold text-[#111D31] tracking-wide leading-none m-0">{detailsModal?.design_no || "N/A"}</h1>
                        <p className="text-[10px] text-[#6A6C70] font-normal tracking-wide m-0 mt-1">{documentInfo?.documentNo || "N/A"}</p>
                    </div>
                </div>
                <button
                    onClick={props.closeModal}
                    className="p-1 text-gray-400 hover:text-gray-700 rounded-full transition-colors flex items-center justify-center mr-1"
                >
                    <X className="w-[20px] h-[20px] text-black" />
                </button>
            </div>

            {/* Stages Timeline List */}
            <div className="flex-1 overflow-y-auto px-[15px] py-[10px] bg-white scrollbar-thin">
                {isLoading ? (
                    <div className="h-full flex items-center justify-center text-[12px] text-[#6A6C70]">
                        Loading stages…
                    </div>
                ) : stages.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                        <NoDataFound />
                    </div>
                ) : stages.map((stage, index) => (
                    <div key={stage.id} className="flex items-center py-[15px] pt-0 relative">
                        {/* Top line connector */}
                        {index > 0 && (
                            <div className={`absolute top-0 bottom-[50%] left-[17px] w-[2px] ${
                                stages[index - 1].status === 'ON TRACK' ? 'bg-[#03B675]' : 'bg-[#F0F3F6]'
                            }`} />
                        )}
                        {/* Bottom line connector */}
                        {index < stages.length - 1 && (
                            <div className={`absolute top-[50%] bottom-0 left-[17px] w-[2px] ${
                                stage.status === 'ON TRACK' ? 'bg-[#03B675]' : 'bg-[#F0F3F6]'
                            }`} />
                        )}

                        {/* Column 1: Dot Icon */}
                        <div className="w-[35px] flex items-center justify-center flex-shrink-0 z-10 mr-[10px]">
                            {getIcon(stage, index)}
                        </div>

                        {/* Column 2: Status Badge */}
                        <div className="w-[100px] flex items-center flex-shrink-0 mr-[10px]">
                            {getStatusBadge(stage.status)}
                        </div>

                        {/* Column 3: Stage Name — min-w-0 + overflow-hidden + break-words
                            keep long names (e.g. "Embroidery/Washing") wrapping/clipped
                            inside their own column instead of bleeding into the User
                            column. min-w-0 is required for flex children to wrap at all. */}
                        <div className="flex-1 min-w-[100px] pr-3 flex items-center overflow-hidden">
                            <span className="text-[12px] font-medium text-[#0F172A] tracking-wide break-words [overflow-wrap:anywhere]">{stage.name}</span>
                        </div>

                        {/* Column 4: User Info — truncate long usernames/emails so
                            they don't push the date column out of the row. */}
                        <div className="w-[120px] flex items-center gap-2 flex-shrink-0 min-w-0">
                            <User className="w-[14px] h-[14px] text-[#707494] flex-shrink-0" />
                            <span
                                className="text-[11px] font-normal text-[#000000] truncate"
                                title={stage.user}
                            >
                                {stage.user}
                            </span>
                        </div>

                        {/* Column 5: Date Column */}
                        <div className="w-[110px] flex items-center justify-end flex-shrink-0 pr-2">
                            <span className="text-[9px] font-normal text-[#6A6C70] whitespace-nowrap">{stage.date}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default DesignStagesModal